function setupEditorialControls(section) {
  const list = section.querySelector('[data-rivio-article-list]');
  if (!list) return;

  const cards = Array.from(list.querySelectorAll('[data-rivio-article-card]'));
  const search = section.querySelector('[data-rivio-search]');
  const sort = section.querySelector('[data-rivio-sort]');

  const filterCards = () => {
    const query = search?.value.trim().toLocaleLowerCase() ?? '';
    cards.forEach((card) => {
      const title = card.dataset.title?.toLocaleLowerCase() ?? '';
      const tags = card.dataset.tags?.toLocaleLowerCase() ?? '';
      const excerpt = card.querySelector('.rivio-editorial-card__body')?.textContent?.toLocaleLowerCase() ?? '';
      card.hidden = !`${title} ${tags} ${excerpt}`.includes(query);
    });
  };

  const sortCards = () => {
    if (!sort) return;
    const direction = sort.value;
    cards.sort((first, second) => {
      if (direction === 'title') {
        return (first.dataset.title ?? '').localeCompare(second.dataset.title ?? '');
      }
      const dateOrder = (first.dataset.published ?? '').localeCompare(second.dataset.published ?? '');
      return direction === 'oldest' ? dateOrder : -dateOrder;
    });
    cards.forEach((card) => list.append(card));
  };

  search?.addEventListener('input', filterCards);
  sort?.addEventListener('change', sortCards);
}

async function fetchEditorialPage(section, targetUrl, shouldPushState = true) {
  const sectionId = section.dataset.sectionId || section.id.replace(/^RivioEditorial-/, '');
  const list = section.querySelector('[data-rivio-article-list]');
  const paginationNav = section.querySelector('.rivio-editorial__pagination');

  section.classList.add('is-loading');
  if (list) list.style.opacity = '0.4';
  if (paginationNav) paginationNav.style.pointerEvents = 'none';

  try {
    let newSection = null;
    const parser = new DOMParser();

    // 1. Try Shopify Section Rendering API with section_id
    if (sectionId) {
      try {
        const sectionApiUrl = new URL(targetUrl, window.location.href);
        sectionApiUrl.searchParams.set('section_id', sectionId);
        const res = await fetch(sectionApiUrl.toString());
        if (res.ok) {
          const html = await res.text();
          const doc = parser.parseFromString(html, 'text/html');
          newSection = doc.querySelector(`[data-section-id="${sectionId}"]`)
            || doc.querySelector(`#RivioEditorial-${sectionId}`)
            || doc.querySelector('[data-rivio-editorial]')
            || (doc.body && doc.body.firstElementChild);
        }
      } catch (e) {
        // Fallback to full page fetch
      }
    }

    // 2. Fallback to full page fetch if section_id did not yield the article grid
    if (!newSection || !newSection.querySelector('[data-rivio-article-list]')) {
      const fullUrl = new URL(targetUrl, window.location.href);
      const res = await fetch(fullUrl.toString());
      if (res.ok) {
        const html = await res.text();
        const doc = parser.parseFromString(html, 'text/html');
        newSection = (sectionId && doc.querySelector(`[data-section-id="${sectionId}"]`))
          || (sectionId && doc.querySelector(`#RivioEditorial-${sectionId}`))
          || doc.querySelector('[data-rivio-editorial]');
      }
    }

    if (newSection) {
      section.innerHTML = newSection.innerHTML;
      setupEditorialControls(section);
      if (shouldPushState) {
        window.history.pushState({ targetUrl }, '', targetUrl);
      }
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.location.href = targetUrl;
    }
  } catch (error) {
    console.error('Error fetching editorial page:', error);
    window.location.href = targetUrl;
  } finally {
    section.classList.remove('is-loading');
    if (list) list.style.opacity = '1';
    if (paginationNav) paginationNav.style.pointerEvents = 'auto';
  }
}

function initRivioPages(root = document) {
  root.querySelectorAll('[data-rivio-editorial]').forEach((section) => {
    if (section.dataset.rivioInitialized) return;
    section.dataset.rivioInitialized = 'true';

    setupEditorialControls(section);

    // Intercept pagination clicks for smooth in-place AJAX loading
    section.addEventListener('click', (event) => {
      const paginationLink = event.target.closest('.rivio-editorial__pagination a');
      if (!paginationLink) return;

      event.preventDefault();
      fetchEditorialPage(section, paginationLink.href, true);
    });
  });

  root.querySelectorAll('[data-rivio-filter-grid]').forEach((section) => {
    if (section.dataset.rivioInitialized) return;
    section.dataset.rivioInitialized = 'true';

    const items = Array.from(section.querySelectorAll('[data-rivio-grid-item]'));
    const search = section.querySelector('[data-rivio-grid-search]');
    const filters = Array.from(section.querySelectorAll('[data-rivio-grid-filter]'));
    const emptyMessage = section.querySelector('[data-rivio-grid-empty]');
    let activeCategory = 'all';

    const updateGrid = () => {
      const query = search?.value.trim().toLocaleLowerCase() ?? '';
      let visibleCount = 0;

      items.forEach((item) => {
        const matchesCategory = activeCategory === 'all' || item.dataset.category === activeCategory;
        const matchesQuery = (item.dataset.search ?? '').toLocaleLowerCase().includes(query);
        item.hidden = !matchesCategory || !matchesQuery;
        if (!item.hidden) visibleCount += 1;
      });

      if (emptyMessage) emptyMessage.hidden = visibleCount > 0;
    };

    filters.forEach((filter) => {
      filter.addEventListener('click', () => {
        activeCategory = filter.dataset.rivioGridFilter ?? 'all';
        filters.forEach((button) => button.classList.toggle('is-active', button === filter));
        updateGrid();
      });
    });

    search?.addEventListener('input', updateGrid);
    updateGrid();
  });

  root.querySelectorAll('.rivio-app-plans').forEach((section) => {
    if (section.dataset.rivioPricingInitialized) return;
    section.dataset.rivioPricingInitialized = 'true';

    const plans = Array.from(section.querySelectorAll('[data-rivio-plan]'));
    const productFilters = Array.from(section.querySelectorAll('[data-rivio-plan-product]'));
    const billingControls = Array.from(section.querySelectorAll('[data-rivio-billing]'));
    let activeProduct = section.dataset.rivioDefaultProduct ?? 'all';
    let activeBilling = 'monthly';

    const updatePlans = () => {
      plans.forEach((plan) => {
        plan.hidden = activeProduct !== 'all' && plan.dataset.product !== activeProduct;
        const price = plan.querySelector('[data-rivio-plan-price]');
        const period = plan.querySelector('[data-rivio-plan-period]');
        const note = plan.querySelector('[data-rivio-plan-note]');
        if (!price || !period || !note) return;

        const billingKey = activeBilling === 'annual' ? 'annual' : 'monthly';
        const value = plan.dataset[`${billingKey}Price`];
        const interval = plan.dataset[`${billingKey}Period`];
        const priceNote = plan.dataset[`${billingKey}Note`];

        if (value) {
          price.textContent = value;
          period.textContent = interval ?? '';
          note.textContent = priceNote ?? '';
          note.hidden = !priceNote;
        } else {
          price.textContent = plan.dataset.monthlyPrice ?? '';
          period.textContent = plan.dataset.monthlyPeriod ?? '';
          note.textContent = 'Annual pricing is not configured for this plan.';
          note.hidden = false;
        }
      });
    };

    productFilters.forEach((button) => {
      button.addEventListener('click', () => {
        activeProduct = button.dataset.rivioPlanProduct ?? 'all';
        productFilters.forEach((control) => {
          const selected = control === button;
          control.classList.toggle('is-active', selected);
          control.setAttribute('aria-pressed', String(selected));
        });
        updatePlans();
      });
    });

    billingControls.forEach((button) => {
      button.addEventListener('click', () => {
        activeBilling = button.dataset.rivioBilling === 'annual' ? 'annual' : 'monthly';
        billingControls.forEach((control) => {
          const selected = control === button;
          control.classList.toggle('is-active', selected);
          control.setAttribute('aria-pressed', String(selected));
        });
        updatePlans();
      });
    });

    productFilters.forEach((button) => {
      const selected = button.dataset.rivioPlanProduct === activeProduct;
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    updatePlans();
  });

  root.querySelectorAll('[data-rivio-article-body]').forEach((body) => {
    if (body.dataset.rivioInitialized) return;
    body.dataset.rivioInitialized = 'true';

    const article = body.closest('.rivio-article');
    const toc = article?.querySelector('[data-rivio-toc]');
    const tocList = toc?.querySelector('[data-rivio-toc-list]');
    if (!toc || !tocList) return;

    const headings = Array.from(body.querySelectorAll('h2, h3'));
    
    // If article body has custom headings differing from default, dynamically re-populate
    if (headings.length > 0 && !body.querySelector('.rivio-guide-step')) {
      tocList.innerHTML = '';
      headings.forEach((heading, index) => {
        if (!heading.id) heading.id = `article-section-${index + 1}`;
        const item = document.createElement('li');
        item.classList.add('rivio-article__toc-item');
        const link = document.createElement('a');
        link.classList.add('rivio-article__toc-link');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent?.trim() ?? '';
        if (heading.tagName === 'H3') item.classList.add('rivio-article__toc-subitem');
        item.append(link);
        tocList.append(item);
      });
      toc.hidden = false;
    }

    // TOC Active State Scroll Spy
    const tocLinks = Array.from(tocList.querySelectorAll('a'));
    const allSections = headings.length > 0 ? headings : Array.from(body.querySelectorAll('.rivio-guide-step, #summary'));

    if (allSections.length > 0 && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const targetId = entry.target.id;
            tocLinks.forEach((link) => {
              if (link.getAttribute('href') === `#${targetId}`) {
                link.classList.add('is-active');
              } else {
                link.classList.remove('is-active');
              }
            });
          }
        });
      }, {
        rootMargin: '-80px 0px -65% 0px',
        threshold: 0
      });

      allSections.forEach((sec) => observer.observe(sec));
    }

    // Smooth scroll for TOC links
    tocLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (href && href.startsWith('#')) {
          const targetEl = document.querySelector(href);
          if (targetEl) {
            e.preventDefault();
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
            tocLinks.forEach(l => l.classList.remove('is-active'));
            link.classList.add('is-active');
            history.pushState(null, '', href);
          }
        }
      });
    });
  });

  // Copy Article Link to Clipboard
  root.querySelectorAll('[data-rivio-copy-url], [data-rivio-share]').forEach((button) => {
    if (button.dataset.rivioCopyInitialized) return;
    button.dataset.rivioCopyInitialized = 'true';

    button.addEventListener('click', async () => {
      const originalHTML = button.innerHTML;
      try {
        await navigator.clipboard.writeText(window.location.href);
        const span = button.querySelector('span');
        if (span) {
          span.textContent = '✓ Copied!';
        } else {
          button.textContent = '✓ Copied!';
        }
        setTimeout(() => {
          button.innerHTML = originalHTML;
        }, 2200);
      } catch {
        const span = button.querySelector('span');
        if (span) span.textContent = 'Copy failed';
      }
    });
  });

  // Copy Admin Path / Snippet Text
  root.querySelectorAll('[data-rivio-copy-text]').forEach((button) => {
    if (button.dataset.rivioCopyTextInitialized) return;
    button.dataset.rivioCopyTextInitialized = 'true';

    button.addEventListener('click', async () => {
      const textToCopy = button.getAttribute('data-rivio-copy-text') || button.textContent;
      const originalText = button.textContent;
      try {
        await navigator.clipboard.writeText(textToCopy);
        button.textContent = '✓ Copied!';
        setTimeout(() => {
          button.textContent = originalText;
        }, 2200);
      } catch {
        button.textContent = 'Copy failed';
      }
    });
  });

  // Print Article Action
  root.querySelectorAll('[data-rivio-print]').forEach((button) => {
    if (button.dataset.rivioPrintInitialized) return;
    button.dataset.rivioPrintInitialized = 'true';

    button.addEventListener('click', () => {
      window.print();
    });
  });

  // Bookmark Article Action
  root.querySelectorAll('[data-rivio-bookmark]').forEach((button) => {
    if (button.dataset.rivioBookmarkInitialized) return;
    button.dataset.rivioBookmarkInitialized = 'true';

    const storageKey = `rivio_bookmark_${window.location.pathname}`;
    if (localStorage.getItem(storageKey) === 'true') {
      button.classList.add('is-active');
    }

    button.addEventListener('click', () => {
      const isActive = button.classList.toggle('is-active');
      localStorage.setItem(storageKey, isActive ? 'true' : 'false');
    });
  });

  // Native Share Dialog
  root.querySelectorAll('[data-rivio-share-dialog]').forEach((button) => {
    if (button.dataset.rivioShareDialogInitialized) return;
    button.dataset.rivioShareDialogInitialized = 'true';

    button.addEventListener('click', async () => {
      if (navigator.share) {
        try {
          await navigator.share({
            title: document.title,
            url: window.location.href
          });
        } catch {
          // Fallback handled by browser or ignored
        }
      } else {
        // Fallback to copy link
        try {
          await navigator.clipboard.writeText(window.location.href);
          alert('Article link copied to clipboard!');
        } catch {
          // ignore
        }
      }
    });
  });

  // Helpful Feedback Widget
  root.querySelectorAll('[data-rivio-feedback]').forEach((button) => {
    if (button.dataset.rivioFeedbackInitialized) return;
    button.dataset.rivioFeedbackInitialized = 'true';

    button.addEventListener('click', () => {
      const card = button.closest('.rivio-article__feedback-card, .rivio-article__feedback');
      const result = card?.querySelector('[data-rivio-feedback-result]');
      const buttons = card?.querySelectorAll('[data-rivio-feedback]');
      
      buttons?.forEach(b => {
        b.style.pointerEvents = 'none';
        b.style.opacity = '0.5';
      });

      if (result) {
        const val = button.getAttribute('data-rivio-feedback');
        if (val === 'yes') {
          result.textContent = '🎉 Thank you! Glad this guide helped your setup.';
        } else {
          result.textContent = 'Thank you for your feedback! Our support engineers review all guide updates.';
        }
        result.hidden = false;
      }
    });
  });

  // Listmonk newsletter subscription handler
  root.querySelectorAll('#listmonk-newsletter-form, .listmonk-newsletter-form, form[action*="/contact#contact_form"], .email-signup__form, .footer__newsletter form').forEach((form) => {
    if (form.dataset.listmonkInitialized) return;
    form.dataset.listmonkInitialized = 'true';

    const emailInput = form.querySelector('input[name="contact[email]"]') || form.querySelector('input[type="email"]');
    const submitButton = form.querySelector('button[type="submit"]') || form.querySelector('input[type="submit"]') || form.querySelector('button');

    let feedbackMsg = form.querySelector('.listmonk-feedback-msg');
    if (!feedbackMsg) {
      feedbackMsg = document.createElement('div');
      feedbackMsg.className = 'listmonk-feedback-msg';
      feedbackMsg.style.marginTop = '10px';
      feedbackMsg.style.fontSize = '14px';
      feedbackMsg.style.lineHeight = '1.4';
      form.appendChild(feedbackMsg);
    }

    const isDark = Boolean(form.closest('.rivio-newsletter') || form.closest('.color-scheme-dark'));

    form.addEventListener('submit', async (e) => {
      if (form._submittingNative) return;
      e.preventDefault();

      const email = emailInput ? emailInput.value.trim() : '';
      if (!email) return;

      if (submitButton) submitButton.disabled = true;
      feedbackMsg.style.color = isDark ? '#94a3b8' : '#555555';
      feedbackMsg.textContent = 'Subscribing...';

      try {
        const response = await fetch('https://list-monk.rivioapps.com/api/public/subscription', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: email,
            name: '',
            list_uuids: ['7dc0d9da-74ff-4366-8e94-8494f5ac3502']
          })
        });

        const data = await response.json();

        if (response.ok && data.data) {
          feedbackMsg.style.color = isDark ? '#34d399' : '#2e7d32';
          feedbackMsg.textContent = 'Thank you for subscribing!';
          if (emailInput) emailInput.value = '';
        } else {
          throw new Error(data.message || 'Subscription failed');
        }
      } catch (err) {
        console.warn('Listmonk direct submit error, falling back to Shopify native submit:', err);
        form._submittingNative = true;
        form.submit();
      } finally {
        if (submitButton) submitButton.disabled = false;
      }
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initRivioPages());
} else {
  initRivioPages();
}

document.addEventListener('shopify:section:load', (e) => {
  initRivioPages(e.target);
});

if (!window._rivioEditorialPopstateBound) {
  window._rivioEditorialPopstateBound = true;
  window.addEventListener('popstate', () => {
    document.querySelectorAll('[data-rivio-editorial]').forEach((section) => {
      fetchEditorialPage(section, window.location.href, false);
    });
  });
}
